import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '../hooks/useTranslation'
import { authFetchJson } from '../lib/authFetch'

// Matches the actual GET /api/v2/people response shape
interface PersonListItem {
  id: number
  name: string
  email: string | null
  title: string | null
  bio: string | null
  linkedin_url: string | null
  orcid: string | null
  tags: string[] | null
  notes: string | null
  created_at: string | null
  updated_at: string | null
}

interface PaginationMeta {
  page: number
  limit: number
  total: number
  pages: number
}

interface PeopleListResponse {
  people: PersonListItem[]
  pagination: PaginationMeta
}

type FilterTab = 'all' | 'researchers'

export function PeoplePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [people, setPeople] = useState<PersonListItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState<FilterTab>('all')
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState<PaginationMeta | null>(null)

  const PAGE_SIZE = 50

  // Fetch people from API
  const fetchPeople = useCallback(async (pageNum: number, query: string) => {
    try {
      setIsLoading(true)
      const params = new URLSearchParams({
        page: String(pageNum),
        limit: String(PAGE_SIZE),
      })
      if (query.trim()) {
        params.set('q', query.trim())
      }
      const data = await authFetchJson<PeopleListResponse>(
        `/api/v2/people?${params.toString()}`
      )
      setPeople(data.people || [])
      setPagination(data.pagination || null)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load people')
      setPeople([])
      setPagination(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchPeople(page, searchQuery)
  }, [page, searchQuery, fetchPeople])

  // Client-side researcher filter (tags-based)
  const filteredPeople =
    activeTab === 'researchers'
      ? people.filter((p) => {
          const tags = Array.isArray(p.tags) ? p.tags : []
          return tags.some(
            (tag) => typeof tag === 'string' && tag.toLowerCase() === 'researcher'
          )
        })
      : people

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setPage(1)
  }

  const isResearcher = (person: PersonListItem): boolean => {
    const tags = Array.isArray(person.tags) ? person.tags : []
    return tags.some(
      (tag) => typeof tag === 'string' && tag.toLowerCase() === 'researcher'
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border-subtle bg-bg-surface p-6">
        <h1 className="text-h1 text-text-primary mb-2">People Directory</h1>
        <p className="text-body text-text-secondary">
          Researchers, faculty, contacts, and partners across the network
        </p>
      </div>

      {/* Search & Filter Tabs */}
      <div className="border-b border-border-subtle bg-bg-body p-6 space-y-4">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="w-full px-3 py-2 bg-bg-surface border border-border-subtle rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary-red"
        />

        {/* All / Researchers tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              activeTab === 'all'
                ? 'bg-primary-red text-white'
                : 'bg-bg-surface border border-border-subtle text-text-secondary hover:bg-border-subtle'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setActiveTab('researchers')}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              activeTab === 'researchers'
                ? 'bg-primary-red text-white'
                : 'bg-bg-surface border border-border-subtle text-text-secondary hover:bg-border-subtle'
            }`}
          >
            Researchers
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-bg-body">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-4">
              <div className="w-8 h-8 border-2 border-primary-red border-t-transparent rounded-full animate-spin" />
              <span className="text-body text-text-secondary">Loading...</span>
            </div>
          </div>
        ) : error ? (
          <div className="p-6">
            <div className="rounded-lg bg-bg-surface border border-border-subtle p-4">
              <p className="text-body text-red-400">Error: {error}</p>
            </div>
          </div>
        ) : filteredPeople.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-body text-text-secondary mb-2">No people found</p>
              <p className="text-sm text-text-tertiary">
                Try adjusting your search or filters
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border-subtle bg-bg-surface">
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                      Type
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPeople.map((person) => (
                    <tr
                      key={person.id}
                      onClick={() => navigate(`/people/${person.id}`)}
                      className="border-b border-border-subtle hover:bg-bg-surface cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-body font-medium text-text-primary">
                          {person.name}
                        </p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-body text-text-secondary">
                          {person.email || '\u2014'}
                        </p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-body text-text-secondary">
                          {person.title || '\u2014'}
                        </p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isResearcher(person) ? (
                          <span className="inline-block px-2.5 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-xs text-emerald-400 font-medium">
                            Researcher
                          </span>
                        ) : (
                          <span className="text-body text-text-tertiary">
                            Contact
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-border-subtle bg-bg-surface">
                <p className="text-sm text-text-tertiary">
                  Showing {(pagination.page - 1) * pagination.limit + 1}
                  {'\u2013'}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="px-3 py-1.5 text-sm bg-bg-surface border border-border-subtle rounded-lg text-text-secondary hover:bg-border-subtle disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                    disabled={page >= pagination.pages}
                    className="px-3 py-1.5 text-sm bg-bg-surface border border-border-subtle rounded-lg text-text-secondary hover:bg-border-subtle disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
